import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { persistQueryClient } from '@tanstack/react-query-persist-client'
import { RouterProvider } from '@tanstack/react-router'
import { del, get, set } from 'idb-keyval'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { UpdatePrompt } from '@/components/app/update-prompt'
import { env } from '@/lib/env'
import { initNative, isNativeApp } from '@/lib/native'
import { initSentry } from '@/lib/sentry'
import { supabase } from '@/lib/supabase'
import { applyTheme } from '@/lib/theme'
import { registerReluxHandlers } from '@/features/relux/submit'
import { registerOutboxHandlers } from '@/offline/handlers'
import { createAppRouter } from '@/router'
import '@/styles/app.css'

initSentry()
applyTheme()
registerOutboxHandlers()
registerReluxHandlers()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cached reads stay usable offline for a week; refreshed in the background when online.
      gcTime: 7 * 24 * 60 * 60_000,
      staleTime: 60_000,
      retry: (count, error) => count < 2 && !(error as { code?: string })?.code?.startsWith('42'),
      networkMode: 'offlineFirst',
    },
    mutations: { networkMode: 'offlineFirst' },
  },
})

const persister = createAsyncStoragePersister({
  key: 'mezsites.query-cache',
  storage: {
    getItem: (key) => get<string>(key).then((v) => v ?? null),
    setItem: (key, value) => set(key, value),
    removeItem: (key) => del(key),
  },
  throttleTime: 1000,
})

const [, restored] = persistQueryClient({
  queryClient,
  persister,
  maxAge: 7 * 24 * 60 * 60_000,
  buster: env.version,
})

const router = createAppRouter(queryClient)
void initNative(router)
// A reset link signs the user in for recovery: always make them set a password first.
supabase.auth.onAuthStateChange((event) => {
  if (event === 'PASSWORD_RECOVERY' && router.state.location.pathname !== '/reset-password')
    void router.navigate({ to: '/reset-password', search: { mode: 'link' } })
})
if (isNativeApp())
  void import('@/features/notifications/native-push').then((m) =>
    m.initNativePush((url) => void router.navigate({ to: url })),
  )

// Restore the cache before the first route loads so the app can start with no signal.
restored.finally(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <UpdatePrompt />
      </QueryClientProvider>
    </StrictMode>,
  )
})
